import type { JSX } from 'react';
import React from 'react';
import ReactDOM from 'react-dom/client';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { BrowserRouter } from 'react-router-dom';
import { App as AntApp, ConfigProvider, message, theme } from 'antd';
import ruRU from 'antd/locale/ru_RU';

import { App } from './App';
import './styles/global.css';
import './styles/authoring.css';
import './editor/editor.css';
import { useThemeMode } from './lib/theme';
import { installOfflineAttemptSync } from './lib/offline-attempts';
import { installUnsavedGuards, syncPreviewFromUrl } from './lib/preview';
import { installPasteFixes } from './lib/paste-fixes';

// Фаза 2.7: досылка ответов теста, сохранённых без сети.
// Предпросмотр черновика курса (?preview=1 в новой вкладке) и защита
// несохранённых правок конструктора (beforeunload + внутренние ссылки).
syncPreviewFromUrl();
installUnsavedGuards();
installOfflineAttemptSync((n) => { console.info(`[wiki] отправлено ответов теста из оффлайн-очереди: ${n}`); });
// Ctrl+V на Mac и диктовка мимо React — во всех полях вики (владелец 30.10.2026).
installPasteFixes({ onError: (msg) => void message.warning(msg) });

// PWA: регистрируем service worker (offline-кеш прочитанных статей +
// картинок + web-push). Не в localhost (в dev SW мешает HMR).
if (
  'serviceWorker' in navigator &&
  !['localhost', '127.0.0.1'].includes(window.location.hostname)
) {
  window.addEventListener('load', () => {
    navigator.serviceWorker.register('/sw.js').catch(() => {
      /* регистрация не критична — приложение работает и без SW */
    });
  });
}

/**
 * QueryClient общий на весь app. Wiki — read-mostly, поэтому ставим
 * staleTime 30 сек: не дёргать API лишний раз пока партнёр читает статью.
 * Если он переключается между блоками — кеш сразу отдаст список без
 * loader'а.
 */
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      gcTime: 5 * 60 * 1000,
      refetchOnWindowFocus: false,
      retry: 1,
    },
  },
});

/** Корень с темой (фаза 1.6): antd-алгоритм и data-theme на <html> из lib/theme. */
function Root(): JSX.Element {
  const { dark } = useThemeMode();
  return (
    <ConfigProvider
      locale={ruRU}
      theme={{
        // FISHKA HUB 2.0 (04.10.2026, _ds): основной цвет — оранжевый fishka
        // (кнопки, вкладки, переключатели, чипы), радиусы 6 / 8, шрифт Manrope.
        // 'FK Emoji' первым — одинаковые эмодзи на любой ОС (см. global.css).
        token: {
          colorPrimary: '#FF6900',
          colorInfo: '#0096FF',
          colorSuccess: '#7CB342',
          colorWarning: '#FB8C00',
          colorError: '#E53935',
          colorLink: '#FF6900',
          colorLinkHover: '#E85F00',
          borderRadius: 6,
          borderRadiusLG: 8,
          borderRadiusSM: 4,
          fontFamily:
            '"FK Emoji", "Manrope", "Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, "Apple Color Emoji", "Noto Color Emoji", sans-serif',
        },
        algorithm: dark ? theme.darkAlgorithm : theme.defaultAlgorithm,
      }}
    >
      {/* antd <App>: контекст для App.useApp() — modal/message с темой
          ConfigProvider. БЕЗ него modal.confirm() в ArticlePage
          молча ничего не делает (antd отдаёт заглушку и warning в консоль). */}
      <AntApp>
        <QueryClientProvider client={queryClient}>
          <BrowserRouter>
            <App />
          </BrowserRouter>
        </QueryClientProvider>
      </AntApp>
    </ConfigProvider>
  );
}

/**
 * Фрейм SCORM-курса (/scorm-run/…) обслуживает /scorm-sw.js. Если SW не
 * перехватил запрос (первая установка, жёсткая перезагрузка) — nginx отдал
 * SPA. Перезагружаем фрейм один раз (теперь SW активен), иначе — подсказка.
 */
function isUnservedScormFrame(): boolean {
  if (!window.location.pathname.startsWith('/scorm-run/')) return false;
  const key = `scorm-run-reload:${window.location.pathname}`;
  let reloaded = false;
  try {
    reloaded = sessionStorage.getItem(key) === '1';
    if (!reloaded) sessionStorage.setItem(key, '1');
  } catch {
    reloaded = true;
  }
  if (!reloaded) window.location.reload();
  else document.getElementById('root')!.textContent = 'Курс не загрузился. Обновите страницу урока.';
  return true;
}

if (!isUnservedScormFrame()) {
  ReactDOM.createRoot(document.getElementById('root')!).render(
    <React.StrictMode>
      <Root />
    </React.StrictMode>,
  );
}
